import { AxiosError } from 'axios'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { task } from 'mobx-task'
import { Subscription, distinctUntilChanged } from 'rxjs'
import { func, validationContext } from 'validx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { UserSource } from 'iam/dtos/member'
import MemberStore from 'iam/MemberStore'
import Member from 'iam/models/Member'
import Role from 'iam/models/Role'
import { SearchableUser } from 'iam/models/SearchableUser'
import Skill from 'iam/models/Skill'
import Team from 'iam/models/Team'
import TeamRole from 'iam/models/TeamRole'
import Workspace from 'iam/models/Workspace'
import RoleStore from 'iam/RoleStore'
import SkillStore from 'iam/SkillStore'
import TeamStore from 'iam/TeamStore'
import TeamRoleStore from 'iam/TeamRoleStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import ModalViewModel from 'infra/ModalViewModel'
import { Id, Nullable } from 'types/definitions'
import { tapAction, toStream } from 'utils/mobx-util'

interface AddWorkspaceMemberResult {
  member?: Member
  success?: boolean
  error?: AxiosError
}

export class AddUserToWorkspaceViewModel extends ModalViewModel {
  /**
   * Subscription to stream of changes to observables.
   */
  private subscription = new Subscription()

  /**
   * The currently selected workspace.
   */
  @observable
  workspace: Nullable<Workspace> = null

  /**
   * The currently selected workspace role.
   */
  @observable
  workspaceRole: Nullable<Role> = null

  /**
   * The currently selected skills.
   */
  @observable
  skills: Skill[] = []

  /**
   * The currently selected team.
   */
  @observable
  team: Nullable<Team> = null

  /**
   * The currently selected team role.
   */
  @observable
  teamRole: Nullable<TeamRole> = null

  /**
   * The list of workspace roles.
   */
  @observable
  workspaceRoles: Role[] = []

  /**
   * The list of available skills.
   */
  @observable
  availableSkills: Skill[] = []

  /**
   * The list of teams.
   */
  @observable
  teams: Team[] = []

  /**
   * The list of team roles.
   */
  @observable
  teamRoles: TeamRole[] = []

  /**
   * Validation rules for the add user to workspace form.
   */
  validation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    workspaceRole: [
      func(
        () => this.workspaceRole !== null,
        'You must select a workspace role.',
      ),
    ],
    teamRole: [
      func(
        () => this.team === null || this.teamRole !== null,
        'If you select a team, you must also select a team role.',
      ),
    ],
  })

  /**
   * Stream of changes to the add user to workspace form inputs.
   */
  private addUserToWorkspaceChanges$ = toStream(
    () => this.workspace,
    false,
  ).pipe(
    distinctUntilChanged(),
    tapAction((workspace) => {
      this.clearSelections()

      if (workspace) {
        this.updateOptions()
      }
    }),
  )

  constructor(
    private _user: () => SearchableUser,
    private _refreshMemberships: () => Promise<void>,
    private newAlert: AlertFactory,
    private memberStore: MemberStore,
    private roleStore: RoleStore,
    private skillStore: SkillStore,
    private teamStore: TeamStore,
    private teamRoleStore: TeamRoleStore,
    private workspaceMembershipStore: WorkspaceMembershipStore,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Activates the view model.
   */
  activate() {
    this.subscription = this.addUserToWorkspaceChanges$.subscribe()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
    this.reset()
  }

  /**
   * The currently selected user.
   */
  @computed
  get user() {
    return this._user()
  }

  /**
   * The list of workspaces the current user (i.e. the admin) may create members in.
   */
  @computed
  get currentUserWorkspaces() {
    return this.workspaceMembershipStore
      .getCurrentUserWorkspaceMemberships()
      .filter((mb) =>
        mb.member.hasAllPermissions(
          'ADMIN_USER_MODIFY',
          'ADMIN_USER_ROLES_MODIFY',
        ),
      )
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces the current user (i.e. the admin) may create team members in.
   */
  @computed
  get currentUserTeamWorkspaces() {
    return this.workspaceMembershipStore
      .getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.hasPermission('ADMIN_MANAGE_TEAM_MEMBER'))
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces the currently selected user (i.e. the one being viewed)
   * is already a member of.
   */
  @computed
  get selectedUserWorkspaces() {
    return this.user.memberships.map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces that the current user may create members in
   * AND that the currently selected user is NOT already a member of.
   */
  @computed
  get availableWorkspaces() {
    return this.currentUserWorkspaces.filter(
      (cuws) =>
        !this.selectedUserWorkspaces.some((suws) => suws.id === cuws.id),
    )
  }

  /**
   * Set the currently selected workspace.
   */
  @action.bound
  setWorkspace(workspace: Nullable<Workspace>) {
    this.workspace = workspace
  }

  /**
   * Set the currently selected workspace role.
   */
  @action.bound
  setWorkspaceRole(role: Nullable<Role>) {
    this.workspaceRole = role
  }

  /**
   * Set the currently selected skills.
   */
  @action.bound
  setSkills(skills: Skill[]) {
    this.skills = skills
  }

  /**
   * Set the currently selected team.
   */
  @action.bound
  setTeam(team: Nullable<Team>) {
    this.team = team
  }

  /**
   * Set the currently selected team role.
   */
  @action.bound
  setTeamRole(role: Nullable<TeamRole>) {
    this.teamRole = role
  }

  /**
   * Set the list of workspace roles.
   */
  @action.bound
  setWorkspaceRoles(roles: Role[]) {
    this.workspaceRoles = roles
  }

  /**
   * Set the list of available skills.
   */
  @action.bound
  setAvailableSkills(skills: Skill[]) {
    this.availableSkills = skills
  }

  /**
   * Set the list of teams.
   */
  @action.bound
  setTeams(teams: Team[]) {
    this.teams = teams
  }

  /**
   * Set the list of team roles.
   */
  @action.bound
  setTeamRoles(roles: TeamRole[]) {
    this.teamRoles = roles
  }

  /**
   * Refreshes the user's workspace memberships.
   */
  async refreshMemberships() {
    await this._refreshMemberships()
  }

  /**
   * Update the list of teams and team roles based on the selected workspace.
   */
  @action.bound
  async updateOptions() {
    if (!this.workspace) {
      return
    }

    await this.roleStore.find({ workspace_id: Id.assert(this.workspace.id) })
    await this.skillStore.find({ workspace_id: Id.assert(this.workspace.id) })
    await this.teamStore.findTeams({
      workspace_id: Id.assert(this.workspace.id),
    })
    await this.teamRoleStore.find({
      workspace_id: Id.assert(this.workspace.id),
    })

    this.setWorkspaceRoles(
      this.roleStore.forWorkspace(Id.assert(this.workspace.id)),
    )
    this.setAvailableSkills(
      this.skillStore.forWorkspace(Id.assert(this.workspace.id)),
    )
    this.setTeams(
      this.teamStore.teams.items.filter(
        // We just confirmed that the workspace is there.
        (t) => t.workspace.id === this.workspace!.id,
      ),
    )
    this.setTeamRoles(
      this.teamRoleStore.forWorkspace(Id.assert(this.workspace.id)),
    )
  }

  /**
   * Clear relevant selections when a new workspace is selected.
   */
  @action.bound
  clearSelections() {
    this.setWorkspaceRole(null)
    this.setSkills([])
    this.setTeam(null)
    this.setTeamRole(null)
  }

  /**
   * Reset the view model to its default state.
   */
  @action.bound
  reset() {
    this.setWorkspace(null)
    this.setWorkspaceRole(null)
    this.setSkills([])
    this.setTeam(null)
    this.setTeamRole(null)

    this.setWorkspaceRoles([])
    this.setAvailableSkills([])
    this.setTeams([])
    this.setTeamRoles([])

    this.validation.reset()
  }

  /**
   * Add the currently selected user to the selected workspace
   */
  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const result: AddWorkspaceMemberResult = await this.memberStore
      .create({
        user_id: Id.assert(this.user.publicId),
        workspace_id: Id.assert(this.workspace!.id),
        given_name: this.user.givenName,
        family_name: this.user.familyName,
        email: this.user.email,
        phone: this.user.phone,
        role_id: Id.assert(this.workspaceRole!.id),
        skill_ids: this.skills.map((s) => Id.assert(s.id)),
        team_id: this.team ? Id.assert(this.team.id) : undefined,
        team_role_id: this.teamRole ? Id.assert(this.teamRole.id) : undefined,
        source: UserSource.Local,
      })
      .then((result) => {
        return { member: result, success: true }
      })
      .catch((err) => {
        return { error: err }
      })

    if (result.success) {
      const displayName = result.member?.displayName
      const workspaceName = this.workspace?.name
      const roleName = this.workspaceRole?.name

      this.refreshMemberships()

      this.newAlert()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `Added ${displayName} to ${workspaceName} as ${roleName}.`,
        )
        .autoDismiss(2500)
        .show()

      this.close()
    } else if (result.error) {
      console.log(result)
      this.newAlert()
        .withState(AlertState.Error)
        .saying('Failed to add user to workspace.', `${result.error.message}`)
        .autoDismiss(2500)
        .show()
    }
  })

  /**
   * Open the dialog and activate the view model.
   */
  @override
  show() {
    this.activate()
    super.show()
  }

  /**
   * Close the dialog and reset selections to default.
   */
  @override
  close() {
    super.close()
    this.deactivate()
  }
}
